import React, { useState } from "react";
import cn from "classnames";
import styles from "./RangeBulletChartVertical.module.scss";
import { useDelay, useSmallScreen } from "@widgets/utils";

export interface RangeBulletChartVerticalProps {
  s: number;
  m: number;
  i: number;
  max: number;
  instrumentTicker: string;
  sColor: string;
  mColor: string;
  iColor: string;

  valueFormatter?: (value: number) => string;
}

const MIN_DISTANCE = 30; // Minimum pixels between ticks

export const RangeBulletChartVertical = React.memo(
  ({
    s,
    m,
    i,
    max,
    instrumentTicker,
    sColor,
    mColor,
    iColor,
    valueFormatter = (value: number) => `${value}%`,
  }: RangeBulletChartVerticalProps) => {
    // Cap values at 100 and calculate offsets based on 100 as max
    const sOffset = Math.min(s, 100);
    const mOffset = Math.min(m, 100);
    const iOffset = Math.min(i, 100);
    // Sort markers by position to check distances
    const markers = [
      { value: s, offset: sOffset, label: "S", color: sColor },
      { value: m, offset: mOffset, label: "M", color: mColor },
      { value: i, offset: iOffset, label: instrumentTicker, color: iColor },
    ]
      .filter((marker) => !!marker.value) // Filter out undefined values
      .sort((a, b) => a.offset - b.offset);

    // Replace the markers.map with a for loop
    const markerPositions: Array<{
      value: number;
      offset: number;
      label: string;
      position: "left" | "right";
      alignment: "normal" | "opposite";
      color: string;
    }> = [];

    for (let i = 0; i < markers.length; i++) {
      const marker = markers[i];
      const prevProcessedMarker = markerPositions[i - 1];

      const isCloseToPrev =
        prevProcessedMarker &&
        Math.abs(marker.offset - prevProcessedMarker.offset) <
          (MIN_DISTANCE / 400) * 100;

      if (isCloseToPrev) {
        if (prevProcessedMarker.position === "left") {
          markerPositions.push({
            ...marker,
            position: "right",
            alignment: "normal",
          });
          markerPositions[i - 2] = {
            ...markerPositions[i - 2],
            alignment: "opposite",
          };
        } else if (
          prevProcessedMarker.position === "right" &&
          prevProcessedMarker.alignment === "opposite"
        ) {
          markerPositions.push({
            ...marker,
            position: "right",
            alignment: "normal",
          });
        } else {
          markerPositions.push({
            ...marker,
            position: "left",
            alignment: "normal",
          });
        }
      } else {
        markerPositions.push({
          ...marker,
          position: "right",
          alignment: "normal",
        });
      }
    }

    return (
      <div className={cn(styles.around)}>
        <div className={cn(styles.surround)}>
          <div className={cn(styles.bar)}>
            <div className={cn(styles.rangeValues)}>
              <span className={cn(styles.maxValue)}>{valueFormatter(100)}</span>
              <span className={cn(styles.minValue)}>{valueFormatter(0)}</span>
            </div>
          </div>
          <div className={cn(styles.markersArea)}>
            {markerPositions.map((marker, index) => (
              <Marker
                key={marker.label}
                value={marker.value}
                offset={marker.offset}
                label={marker.label}
                position={marker.position}
                alignment={marker.alignment}
                valueFormatter={valueFormatter}
                color={marker.color}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

const Marker = ({
  value,
  offset,
  label,
  position,
  alignment = "normal",
  valueFormatter,
  color,
}: {
  value: number;
  offset: number;
  label: string;
  position: "left" | "right";
  alignment?: "normal" | "opposite";
  valueFormatter: (value: number) => string;
  color: string;
}) => {
  const getColorClass = (color: string) => {
    if (color === "var(--chart-green-color)") return "green";
    if (color === "var(--chart-blue-color)") return "blue";
    return "yellow";
  };

  return (
    <div
      className={cn(styles.marker)}
      style={{
        bottom: `${offset}%`,
        transform: "translateY(50%)", // This centers the marker vertically
      }}
    >
      <div
        className={cn(
          styles.markerContent,
          styles[position],
          styles[alignment]
        )}
      >
        {alignment === "normal" && (
          <div className={styles.labelGroup}>
            <span className={styles.markerLabel}>{label}</span>
            <span className={styles.separator}> </span>
            <span className={styles.markerValue}>{valueFormatter(value)}</span>
          </div>
        )}
        <div className={cn(styles.tick, styles[getColorClass(color)])} />
        {alignment === "opposite" && (
          <div className={cn(styles.labelGroup, styles.opposite)}>
            <span className={styles.markerLabel}>{label}</span>
            <span className={styles.separator}> </span>
            <span className={styles.markerValue}>{valueFormatter(value)}</span>
          </div>
        )}
      </div>
    </div>
  );
};
